import { useState } from "react";
import { Phone, ArrowRight, Clock, Shield, CheckCircle, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TalkToExpert({ placeId, email, phone: initialPhone, name: contactName, className = "" }) {
  const [phone, setPhone] = useState(initialPhone || "");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanPhone = phone.trim().replace(/[^\d+]/g, "");
    if (!cleanPhone || cleanPhone.length < 10) {
      setStatus("error");
      setErrorMsg("Please enter a valid phone number (10+ digits).");
      return;
    }
    setStatus("loading");
    setErrorMsg("");

    try {
      const res = await axios.post(`${API}/vapi/request-call`, {
        phone_number: cleanPhone,
        place_id: placeId,
        email: email || "",
        name: contactName || "",
      });
      if (res.data?.success) {
        setStatus("success");
      } else {
        throw new Error(res.data?.error || "Something went wrong");
      }
    } catch (err) {
      setStatus("error");
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message || "Could not initiate call.";
      setErrorMsg(msg);
    }
  };

  if (status === "success") {
    return (
      <div className={`w-full ${className}`} data-testid="talk-to-expert-success">
        <div style={{ background: "linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)", padding: "32px 20px", textAlign: "center" }}>
          <div style={{ width: 52, height: 52, margin: "0 auto 14px", background: "#DCFCE7", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CheckCircle style={{ width: 26, height: 26, color: "#16a34a" }} />
          </div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: "#0F172A", marginBottom: 8, fontFamily: "'Outfit',sans-serif" }}>Your phone will ring within 10 seconds!</h3>
          <p style={{ fontSize: 13, color: "#64748B", lineHeight: 1.6, fontFamily: "'IBM Plex Sans',sans-serif" }}>Our AI consultant has your complete audit data and will walk you through exactly what to fix.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`} data-testid="talk-to-expert">
      <div style={{ background: "linear-gradient(135deg, #EFF6FF 0%, #F5F3FF 100%)", padding: "32px 16px" }}>
        <div style={{ maxWidth: 440, margin: "0 auto", textAlign: "center" }}>
          <div style={{ width: 44, height: 44, margin: "0 auto 12px", background: "rgba(0,47,167,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Phone style={{ width: 22, height: 22, color: "#002FA7" }} />
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: "#0F172A", marginBottom: 6, fontFamily: "'Outfit',sans-serif" }}>Have questions about your results?</h3>
          <p style={{ fontSize: 13, color: "#64748B", marginBottom: 20, lineHeight: 1.6, fontFamily: "'IBM Plex Sans',sans-serif" }}>
            Talk to an AI consultant who has your complete audit data and can walk you through exactly what to fix.
          </p>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            <div style={{ position: "relative" }}>
              <Phone style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", width: 16, height: 16, color: "#94A3B8" }} />
              <input
                type="tel"
                placeholder="Your phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                style={{
                  width: "100%", height: 48, paddingLeft: 38, paddingRight: 14,
                  border: "1px solid #E2E8F0", background: "#fff", fontSize: 14,
                  color: "#0F172A", outline: "none", fontFamily: "'IBM Plex Sans',sans-serif",
                }}
                data-testid="talk-to-expert-phone-input"
              />
            </div>
            <button
              type="submit"
              disabled={status === "loading" || !phone.trim()}
              style={{
                width: "100%", height: 48, background: "#002FA7", color: "#fff",
                fontWeight: 700, fontSize: 14, border: "none", cursor: status === "loading" ? "wait" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                opacity: (!phone.trim() || status === "loading") ? 0.6 : 1,
                fontFamily: "'IBM Plex Sans',sans-serif",
              }}
              data-testid="talk-to-expert-submit-btn"
            >
              {status === "loading" ? (
                <><Loader2 style={{ width: 16, height: 16, animation: "spin 1s linear infinite" }} /> Connecting...</>
              ) : (
                <>Talk to an Expert <ArrowRight style={{ width: 16, height: 16 }} /></>
              )}
            </button>
          </form>

          {status === "error" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginBottom: 12, padding: "10px 14px", background: "#FEF2F2", border: "1px solid #FECACA" }} data-testid="talk-to-expert-error">
              <AlertCircle style={{ width: 14, height: 14, color: "#E11D48", flexShrink: 0 }} />
              <p style={{ fontSize: 13, color: "#9F1239", margin: 0, fontFamily: "'IBM Plex Sans',sans-serif" }}>{errorMsg}</p>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#64748B", fontFamily: "'IBM Plex Sans',sans-serif" }}>
              <Clock style={{ width: 13, height: 13 }} /> 3-5 min call
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#64748B", fontFamily: "'IBM Plex Sans',sans-serif" }}>
              <Shield style={{ width: 13, height: 13 }} /> No obligation
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
